import { Module } from 'vuex'
import redemptionErrorHandler from '~utils/redemption-error-handler.ts'
import axios from '~plugins/axios'

import { Redemption } from '~models/redemption'

import cancellationUrlWithError from '~utils/psp-cancellation'

const REDIRECT_TIMEOUT_IN_SECONDS = 5
const UNAUTHORIZED_ACCESS_REDIRECT_URL = 'https://lufthansa.com'

interface State {
  loading: boolean;
  paymentProcessing: boolean;
  redemption: Redemption;
  errors: string[];
  ultimateFailureReason?: string;
  keepaliveActive: boolean;
}

const store: Module<State, any> = {
  namespaced: true,
  state: {
    loading: false,
    paymentProcessing: false,
    redemption: JSON.parse(sessionStorage.getItem('redemption') as string) || undefined,
    errors: [],
    ultimateFailureReason: undefined,
    keepaliveActive: false,
  },
  getters: {
    redemption: (state: State): Redemption => state.redemption,
  },
  actions: {
    async getRedemption({ commit, dispatch }, { redemptionId, resetError = true }) {
      if (resetError) {
        commit('RESET_ERRORS')
      }
      commit('SET_LOADING_STATE', { loading: true })

      try {
        const { data } = await axios.get(`/redemptions/${redemptionId}`)

        commit('SET_REDEMPTION', { data })
        dispatch('setupKeepalive')
      } catch (error) {
        dispatch('setErrorsFromResponse', error)
        if (error.response?.status === 404) {
          window.location.assign(UNAUTHORIZED_ACCESS_REDIRECT_URL)
        }
      } finally {
        commit('SET_LOADING_STATE', { loading: false })
      }
    },
    async updateRemption({ commit, dispatch }, { redemptionId, payload }) {
      commit('RESET_ERRORS')
      commit('SET_LOADING_STATE', { loading: true })

      try {
        const { data } = await axios.put(`/redemptions/${redemptionId}/voucher`, payload)

        commit('SET_REDEMPTION', { data })
      } catch (error) {
        dispatch('setErrorsFromResponse', error)
      } finally {
        commit('SET_LOADING_STATE', { loading: false })
      }
    },
    async validateVouchers({ commit, dispatch }, { redemptionId }) {
      commit('RESET_ERRORS')
      commit('SET_LOADING_STATE', { loading: true })

      try {
        await axios.post(`/redemptions/${redemptionId}/validate_vouchers`)
      } catch (error) {
        dispatch('setErrorsFromResponse', error)
        commit('SET_LOADING_STATE', { loading: false })
      }
    },
    async checkout({ commit, dispatch }, {
      redemptionId, datatransTransactionId, redirectRequired, appliedVouchersBalance,
    }) {
      commit('RESET_ERRORS')
      commit('SET_LOADING_STATE', { loading: true })
      commit('SET_PAYMENT_PROCESSING_STATE', { paymentProcessing: true })

      try {
        const { data } = await axios.put(`/redemptions/${redemptionId}/checkout`, {
          datatransTransactionId,
          redirectRequired,
          appliedVouchersBalance,
        })

        commit('SET_REDEMPTION', { data })
      } catch (error) {
        dispatch('setErrorsFromResponse', error)
        commit('SET_LOADING_STATE', { loading: false })
        commit('SET_PAYMENT_PROCESSING_STATE', { paymentProcessing: false })
      }
    },
    async keepalive({ state, dispatch }) {
      try {
        const { data } = await axios.put(`/redemptions/${state.redemption.id}/keepalive`)

        setTimeout(() => {
          dispatch('keepalive')
        }, data.renewableInMilliseconds)
      } catch (err) {
        window.location.assign(state.redemption.cancellationUrl)
      }
    },
    async setupKeepalive({ commit, state, dispatch }) {
      if (!state.keepaliveActive && state.redemption.id) {
        dispatch('keepalive')
        commit('SET_KEEPALIVE_ACTIVE')
      }
    },
    async secure3dRequired({ commit, dispatch }, { redemptionId, datatransTransactionId }) {
      commit('RESET_ERRORS')
      commit('SET_LOADING_STATE', { loading: true })
      commit('SET_PAYMENT_PROCESSING_STATE', { paymentProcessing: true })

      try {
        const response = await axios.get(`/redemptions/${redemptionId}/3d_secure_required`, {
          params: { datatrans_transaction_id: datatransTransactionId },
        })
        commit('SET_REDEMPTION', { data: { secure3dRequired: response.data.redirect } })
      } catch (error) {
        dispatch('setErrorsFromResponse', error)
      } finally {
        commit('SET_LOADING_STATE', { loading: false })
        commit('SET_PAYMENT_PROCESSING_STATE', { paymentProcessing: false })
      }
    },
    async initializeDatatrans({ commit, dispatch }, { redemptionId }) {
      commit('SET_LOADING_STATE', { loading: true })

      try {
        const { data } = await axios.put(`/redemptions/${redemptionId}/initialize_datatrans`)

        commit('SET_REDEMPTION', { data })
      } catch (error) {
        dispatch('setErrorsFromResponse', error)
      } finally {
        commit('SET_LOADING_STATE', { loading: false })
      }
    },
    failRedemption({ commit, state }, { reason }) {
      commit('SET_ULTIMATELY_FAILED', reason)

      setTimeout(() => {
        const redirectUrl = cancellationUrlWithError(
          state.redemption.cancellationUrl,
          state.ultimateFailureReason as string,
        )
        window.location.assign(redirectUrl)
      }, REDIRECT_TIMEOUT_IN_SECONDS * 1000)
    },
    setLoadingState({ commit }, { loading }) {
      commit('SET_LOADING_STATE', { loading })
    },
    setPaymentProcessingState({ commit }, { paymentProcessing }) {
      commit('SET_PAYMENT_PROCESSING_STATE', { paymentProcessing })
    },
    setErrorsFromResponse({ commit, dispatch }, error) {
      const errors = redemptionErrorHandler(error, dispatch)
      commit('SET_ERRORS', errors)
    },
    setErrors({ commit }, { errors }) {
      commit('SET_ERRORS', errors)
    },
    setRedemption({ commit }, data) {
      commit('SET_REDEMPTION', { data })
    },
  },
  mutations: {
    SET_LOADING_STATE(state, { loading }) {
      state.loading = loading
    },
    SET_PAYMENT_PROCESSING_STATE(state, { paymentProcessing }) {
      state.paymentProcessing = paymentProcessing
    },
    SET_REDEMPTION(state, { data }) {
      state.redemption = {
        ...state.redemption,
        ...data,
      }

      sessionStorage.setItem('redemption', JSON.stringify(state.redemption))
    },
    RESET_ERRORS(state) {
      state.errors = []
    },
    SET_ERRORS(state, errors) {
      state.errors = errors
    },
    SET_ULTIMATELY_FAILED(state: State, reason: string): void {
      state.ultimateFailureReason = reason
    },
    SET_KEEPALIVE_ACTIVE(state: State): void {
      state.keepaliveActive = true
    },
  },
}

export default store
